import React, { useEffect, useMemo, useState } from 'react';
import { authenticatedFetch } from '../../utils/auth';
import './AbastecimentoModule.css';
import { saveAs } from 'file-saver';
import { criarRelatorioExcel, RelatorioItem } from './relatorioExcel';

interface VehicleTypeInfo {
  TIPO_VEICULO?: string;
  MODELO_VEICULO?: string;
  META1?: number;
  META2?: number;
  META3?: number;
  META1_ARLA?: number;
  META2_ARLA?: number;
  META3_ARLA?: number;
}

interface HistoricoItem {
  cd_ticket_nucci?: number;
  dt_create?: string;
  dt_abastecimento?: string;
  dt_data_abastecimento?: string;
  nm_tipo_veiculo?: string;
  cd_litros?: number;
  cd_valor_real?: number;
  cd_valor_litro?: number;
  cd_hodometro_anterior?: number;
  cd_hodometro_atual?: number;
  cd_consumo_anterior?: number;
  cd_consumo_atual?: number;
  META1?: number;
  META2?: number;
  META3?: number;
}

interface MotoristaOption {
  nome: string;
  cpf: string;
  label: string;
}

interface TicketHodometro {
  cd_abastecimento: number;
  cd_ticket_nucci?: number;
  data_referencia: string;
  cd_hodometro_anterior: number;
  cd_hodometro_atual: number;
  cd_distancia?: number;
}

interface HodometroProblema {
  tipoProblema: 'REGRESSAO_HODOMETRO' | 'CADEIA_DIVERGENTE';
  placa: string;
  tipoAbastecimento: string;
  orientacao: string;
  registroReferencia: TicketHodometro;
  primeiroTicketProblema: TicketHodometro;
  ticketsImpactados: TicketHodometro[];
  totalTicketsAfetados: number;
}

const getNowLocalDateTime = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  const local = new Date(now.getTime() - offsetMs);
  return local.toISOString().slice(0, 16);
};

const toDateTimeLocalValue = (value?: string) => {
  if (!value) return '';

  const normalized = String(value).trim();
  if (!normalized) return '';

  // Ja esta em formato de input datetime-local
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(normalized)) {
    return normalized.slice(0, 16);
  }

  // Data sem horario: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return `${normalized}T00:00`;
  }

  const asDate = new Date(normalized);
  if (Number.isNaN(asDate.getTime())) {
    return '';
  }

  const offsetMs = asDate.getTimezoneOffset() * 60000;
  const local = new Date(asDate.getTime() - offsetMs);
  return local.toISOString().slice(0, 16);
};

const toNumber = (value: string, defaultValue = 0) => {
  const n = Number(String(value || '').replace(',', '.'));
  return Number.isFinite(n) ? n : defaultValue;
};

const formatNumber = (value?: number | string | null, fractionDigits = 2) => {
  if (value === undefined || value === null || value === '') return '-';
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);
  return numberValue.toLocaleString('pt-BR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
};

const formatCurrency = (value?: number | string | null) => {
  if (value === undefined || value === null || value === '') return '-';
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);
  return numberValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const formatDecimalInput = (value?: number | string | null, fractionDigits = 3) => {
  if (value === undefined || value === null || value === '') return '';
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);
  return numberValue.toLocaleString('pt-BR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const asDate = new Date(value);
  if (Number.isNaN(asDate.getTime())) return String(value);
  return asDate.toLocaleString('pt-BR');
};

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const fallbackCopyText = (text: string) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  textarea.style.top = '0';
  textarea.style.left = '0';

  document.body.appendChild(textarea);

  const selection = document.getSelection();
  const currentRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);

  if (selection) {
    selection.removeAllRanges();
    if (currentRange) {
      selection.addRange(currentRange);
    }
  }

  return copied;
};

const normalizeCpf = (value?: string) => String(value || '').replace(/\D/g, '');

const formatCpf = (value?: string) => {
  const digits = normalizeCpf(value);
  if (digits.length !== 11) {
    return digits || '';
  }

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const splitMotoristaLabel = (value: string) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return { nome: '', cpf: '' };
  }

  const sep = raw.lastIndexOf(' - ');
  if (sep < 0) {
    return { nome: raw, cpf: '' };
  }

  return {
    nome: raw.slice(0, sep).trim(),
    cpf: normalizeCpf(raw.slice(sep + 3).trim())
  };
};

const isCpfValue = (value: string) => normalizeCpf(value).length === 11;

const getInitialFormState = () => ({
  placa: '',
  arla: false,
  tipoVeiculo: '',
  meta3: '',
  dataAbastecimento: getNowLocalDateTime(),
  litrosAbastecidos: '',
  valorLitros: '',
  hodometro: '',
  consumoAnterior: '',
  motorista: '',
  motoristaCpf: '',
  posto: '',
  operacao: '',
  dataAtual: getNowLocalDateTime(),
  hodometroAtual: '',
  valorDiesel: '',
  litros: '',
  litrosReais: ''
});

const AbastecimentoModule: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [placas, setPlacas] = useState<string[]>([]);
  const [motoristas, setMotoristas] = useState<MotoristaOption[]>([]);
  const [postos, setPostos] = useState<string[]>([]);

  const [vehicleType, setVehicleType] = useState<VehicleTypeInfo | null>(null);
  const [historico, setHistorico] = useState<HistoricoItem | null>(null);
  const [showAprovacaoModal, setShowAprovacaoModal] = useState(false);
  const [observacaoAprovacao, setObservacaoAprovacao] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const [showRelatorioModal, setShowRelatorioModal] = useState(false);
  const [showProblemasModal, setShowProblemasModal] = useState(false);
  const [problemasHodometro, setProblemasHodometro] = useState<HodometroProblema[]>([]);
  const [loadingProblemas, setLoadingProblemas] = useState(false);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [exportandoRelatorio, setExportandoRelatorio] = useState(false);
  const [relatorioRows, setRelatorioRows] = useState<RelatorioItem[]>([]);
  const [filtrosRelatorio, setFiltrosRelatorio] = useState({
    dataInicio: todayIsoDate(),
    dataFim: todayIsoDate(),
    placa: '',
    operacao: ''
  });

  const [form, setForm] = useState(getInitialFormState);

  const getCpfPrefixMatches = (cpfDigits: string) => {
    if (!cpfDigits) {
      return [] as MotoristaOption[];
    }

    return motoristas.filter((item) => item.cpf.startsWith(cpfDigits));
  };

  const findMotoristaOption = (inputValue: string) => {
    const raw = String(inputValue || '').trim();
    if (!raw) {
      return null;
    }

    const rawLower = raw.toLowerCase();
    const rawCpf = normalizeCpf(raw);

    const exact = motoristas.find((item) => {
      if (item.label.toLowerCase() === rawLower) return true;
      if (item.nome.toLowerCase() === rawLower) return true;
      if (rawCpf && item.cpf === rawCpf) return true;
      return false;
    }) || null;

    if (exact) {
      return exact;
    }

    if (rawCpf.length >= 6) {
      const prefixMatches = getCpfPrefixMatches(rawCpf);
      if (prefixMatches.length === 1) {
        return prefixMatches[0];
      }
    }

    return null;
  };

  const applyMotoristaOption = (option: MotoristaOption) => {
    setForm((prev) => ({
      ...prev,
      motorista: option.nome,
      motoristaCpf: option.cpf
    }));
  };

  const clearMotoristaSelection = () => {
    setForm((prev) => ({
      ...prev,
      motorista: '',
      motoristaCpf: ''
    }));
  };

  const confirmMotoristaInput = (inputValue: string, showError = true) => {
    const raw = String(inputValue || '').trim();
    const rawCpf = normalizeCpf(raw);
    if (!raw) {
      clearMotoristaSelection();
      return;
    }

    const found = findMotoristaOption(raw);
    if (found) {
      applyMotoristaOption(found);
      return;
    }

    if (rawCpf.length >= 6) {
      const prefixMatches = getCpfPrefixMatches(rawCpf);
      if (prefixMatches.length > 1) {
        clearMotoristaSelection();
        if (showError) {
          setError('Existe mais de um motorista para esse prefixo de CPF. Digite mais numeros para filtrar.');
        }
        return;
      }
    }

    clearMotoristaSelection();
    if (showError) {
      setError('Motorista invalido. Informe um nome/CPF existente na lista e pressione Enter.');
    }
  };

  const clearAlerts = () => {
    setMessage('');
    setError('');
  };

  const carregarListasApoio = async () => {
    try {
      const [resPlacas, resMotoristas, resPostos] = await Promise.all([
        fetch('/api/abastecimento/autoCompletePlaca', { method: 'POST' }),
        fetch('/api/abastecimento/autoCompleteMotorista', { method: 'POST' }),
        fetch('/api/abastecimento/listaPostos', { method: 'POST' })
      ]);

      if (resPlacas.ok) {
        const data = await resPlacas.json();
        setPlacas(
          Array.isArray(data)
            ? data
              .map((item: { splaca_veic?: string }) => item.splaca_veic)
              .filter((item): item is string => Boolean(item))
            : []
        );
      }

      if (resMotoristas.ok) {
        const data = await resMotoristas.json();
        setMotoristas(
          Array.isArray(data)
            ? data
              .map((item: { snome_mot?: string; scpf_mot?: string }) => {
                const nome = String(item.snome_mot || '').trim();
                const cpfDigits = normalizeCpf(item.scpf_mot);
                const label = cpfDigits ? `${nome} - ${cpfDigits}` : nome;

                return {
                  nome,
                  cpf: cpfDigits,
                  label
                };
              })
              .filter((item): item is MotoristaOption => Boolean(item.nome))
            : []
        );
      }

      if (resPostos.ok) {
        const data = await resPostos.json();
        setPostos(
          Array.isArray(data)
            ? data
              .map((item: { nm_posto?: string }) => item.nm_posto)
              .filter((item): item is string => Boolean(item))
            : []
        );
      }
    } catch {
      setError('Nao foi possivel carregar as listas de apoio.');
    }
  };

  useEffect(() => {
    carregarListasApoio();
  }, []);

  const carregarProblemasHodometro = async (abrirModal = false) => {
    try {
      setLoadingProblemas(true);
      const response = await authenticatedFetch('/api/abastecimento/inconsistenciasHodometro');
      if (!response.ok) throw new Error('Falha ao consultar inconsistencias');
      const data = await response.json();
      const rows: HodometroProblema[] = Array.isArray(data) ? data : [];
      setProblemasHodometro(rows);
      if (abrirModal) setShowProblemasModal(true);
    } catch {
      setError('Nao foi possivel consultar os problemas de hodometro.');
    } finally {
      setLoadingProblemas(false);
    }
  };

  useEffect(() => {
    if (!vehicleType) {
      return;
    }

    const metaSelecionada = form.arla
      ? (vehicleType.META3_ARLA ?? vehicleType.META3)
      : (historico?.META3 ?? vehicleType.META3);

    if (metaSelecionada === undefined || metaSelecionada === null) {
      return;
    }

    setForm((prev) => {
      const nextMeta = String(metaSelecionada);
      if (prev.meta3 === nextMeta) {
        return prev;
      }
      return {
        ...prev,
        meta3: nextMeta
      };
    });
  }, [form.arla, vehicleType, historico?.META3]);

  const buscarDadosPlaca = async () => {
    clearAlerts();
    if (!form.placa) {
      setError('Informe a placa para buscar os dados.');
      return;
    }

    try {
      setLoading(true);
      const tipoAbastecimento = form.arla ? 'Arla' : 'Diesel';

      const [resVehicleType, resPreticket] = await Promise.all([
        fetch('/api/abastecimento/checkVehicleType', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ placa: form.placa })
        }),
        fetch('/api/abastecimento/checkAbastecimento', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ placa: form.placa, tipoAbastecimento })
        })
      ]);

      let vehicleRow: VehicleTypeInfo | null = null;
      if (resVehicleType.ok) {
        const vehicleData = await resVehicleType.json();
        vehicleRow = Array.isArray(vehicleData) && vehicleData.length > 0 ? vehicleData[0] : null;
      }

      let historicoRow: HistoricoItem | null = null;
      if (resPreticket.ok) {
        const preticketData = await resPreticket.json();
        historicoRow = Array.isArray(preticketData) && preticketData.length > 0 ? preticketData[0] : null;
      }

      const meta3 = form.arla
        ? (vehicleRow?.META3_ARLA ?? vehicleRow?.META3)
        : (historicoRow?.META3 ?? vehicleRow?.META3);

      setVehicleType(vehicleRow);
      setHistorico(historicoRow);
      const dataUltimoAbastecimento = toDateTimeLocalValue(
        historicoRow?.dt_data_abastecimento || historicoRow?.dt_abastecimento || historicoRow?.dt_create
      );
      const litragemHistorica =
        historicoRow?.cd_valor_real !== undefined && historicoRow?.cd_valor_real !== null
          ? String(historicoRow.cd_valor_real)
          : historicoRow?.cd_litros !== undefined && historicoRow?.cd_litros !== null
            ? String(historicoRow.cd_litros)
            : '';
      setForm((prev) => ({
        ...prev,
        tipoVeiculo: historicoRow?.nm_tipo_veiculo || vehicleRow?.TIPO_VEICULO || prev.tipoVeiculo,
        meta3: meta3 !== undefined && meta3 !== null ? String(meta3) : prev.meta3,
        hodometro:
          historicoRow?.cd_hodometro_atual !== undefined && historicoRow?.cd_hodometro_atual !== null
            ? String(historicoRow.cd_hodometro_atual)
            : historicoRow?.cd_hodometro_anterior !== undefined && historicoRow?.cd_hodometro_anterior !== null
              ? String(historicoRow.cd_hodometro_anterior)
            : prev.hodometro,
        consumoAnterior:
          historicoRow?.cd_consumo_atual !== undefined && historicoRow?.cd_consumo_atual !== null
            ? formatDecimalInput(historicoRow.cd_consumo_atual)
            : historicoRow?.cd_consumo_anterior !== undefined && historicoRow?.cd_consumo_anterior !== null
              ? formatDecimalInput(historicoRow.cd_consumo_anterior)
            : prev.consumoAnterior,
        litrosAbastecidos: litragemHistorica,
        litrosReais: '',
        valorLitros:
          historicoRow?.cd_valor_litro !== undefined && historicoRow?.cd_valor_litro !== null
            ? String(historicoRow.cd_valor_litro)
            : prev.valorLitros,
        dataAbastecimento: dataUltimoAbastecimento || prev.dataAbastecimento
      }));

      setMessage('Dados carregados para a placa informada.');
    } catch {
      setError('Nao foi possivel buscar os dados da placa.');
    } finally {
      setLoading(false);
    }
  };

  const distanciaCalculada = useMemo(() => {
    const anterior = toNumber(form.hodometro, 0);
    const atual = toNumber(form.hodometroAtual, 0);
    const d = atual - anterior;
    return d >= 0 ? d : 0;
  }, [form.hodometro, form.hodometroAtual]);

  const litrosIdeais = useMemo(() => {
    const meta = toNumber(form.meta3, 0);
    if (meta <= 0 || distanciaCalculada <= 0) return null;
    return distanciaCalculada / meta;
  }, [distanciaCalculada, form.meta3]);

  const consumoSistema = useMemo(() => {
    if (litrosIdeais === null || litrosIdeais <= 0) return 0;
    return distanciaCalculada / litrosIdeais;
  }, [distanciaCalculada, litrosIdeais]);

  const consumoReal = useMemo(() => {
    const litrosRealOuAbastecidos = toNumber(form.litrosReais || form.litrosAbastecidos, 0);
    if (litrosRealOuAbastecidos <= 0) return 0;
    return distanciaCalculada / litrosRealOuAbastecidos;
  }, [distanciaCalculada, form.litrosReais, form.litrosAbastecidos]);

  const valorTotalAbastecimento = useMemo(() => {
    const valorLitro = toNumber(form.valorDiesel || form.valorLitros, 0);
    const litrosBase = toNumber(form.litrosReais || form.litrosAbastecidos, 0);
    return valorLitro * litrosBase;
  }, [form.valorDiesel, form.valorLitros, form.litrosReais, form.litrosAbastecidos]);

  const textoWhatsappPreticket = useMemo(() => {
    const linhas = [
      'DADOS DO PRE-TICKET',
      `Placa: ${form.placa || '-'}`,
      `Motorista: ${form.motorista || '-'}`,
      `Tipo de Veiculo: ${form.tipoVeiculo || '-'}`,
      `Tipo de Abastecimento: ${form.arla ? 'Arla' : 'Diesel'}`,
      `Posto: ${form.posto || '-'}`,
      `Operação: ${form.operacao || '-'}`,
      `Litros Desejados: ${litrosIdeais !== null ? `${formatNumber(litrosIdeais)} L` : '-'}`,
      `Litros Realizados: ${form.litrosReais ? `${formatNumber(form.litrosReais)} L` : `${formatNumber(form.litrosAbastecidos)} L`}`,
      `Valor do litro: ${formatCurrency(toNumber(form.valorDiesel || form.valorLitros, 0))}`,
      `Valor Total do Abastecimento: ${formatCurrency(valorTotalAbastecimento)}`,
      `Consumo Anterior: ${formatNumber(form.consumoAnterior, 3)}`,
      `Consumo Desejado: ${formatNumber(form.meta3, 2)}`,
      `Consumo Real: ${formatNumber(consumoReal, 2)}`,
      `Hodometro Atual: ${form.hodometroAtual || '-'}`,
      `Hodometro Anterior: ${form.hodometro || '-'}`,
      `Distancia Percorrida: ${formatNumber(distanciaCalculada, 0)} km`,
      `Observacao: ${observacaoAprovacao.trim() || '-'}`
    ];

    return linhas.join('\n');
  }, [
    form,
    litrosIdeais,
    valorTotalAbastecimento,
    consumoReal,
    distanciaCalculada,
    observacaoAprovacao
  ]);

  const copiarDadosPreticket = async () => {
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(textoWhatsappPreticket);
      } else {
        const copied = fallbackCopyText(textoWhatsappPreticket);
        if (!copied) {
          throw new Error('Fallback copy failed');
        }
      }

      setCopyFeedback('Dados copiados para envio no WhatsApp.');
      window.setTimeout(() => setCopyFeedback(''), 2200);
    } catch {
      try {
        const copied = fallbackCopyText(textoWhatsappPreticket);
        if (copied) {
          setCopyFeedback('Dados copiados para envio no WhatsApp.');
          window.setTimeout(() => setCopyFeedback(''), 2200);
          return;
        }
      } catch {
        // Ignora erro do fallback para exibir orientacao ao usuario.
      }

      setCopyFeedback('Nao foi possivel copiar automaticamente. Tente Ctrl+C manualmente.');
      window.setTimeout(() => setCopyFeedback(''), 2800);
    }
  };

  const isTipoVeiculoLocked = Boolean(historico?.nm_tipo_veiculo || vehicleType?.TIPO_VEICULO);
  const isDataAbastecimentoLocked = Boolean(historico?.dt_create);
  const isLitrosAbastecidosLocked =
    (historico?.cd_valor_real !== undefined && historico?.cd_valor_real !== null) ||
    (historico?.cd_litros !== undefined && historico?.cd_litros !== null);
  const isValorLitrosLocked = historico?.cd_valor_litro !== undefined && historico?.cd_valor_litro !== null;
  const isHodometroLocked =
    (historico?.cd_hodometro_atual !== undefined && historico?.cd_hodometro_atual !== null) ||
    (historico?.cd_hodometro_anterior !== undefined && historico?.cd_hodometro_anterior !== null);
  const isConsumoAnteriorLocked =
    (historico?.cd_consumo_atual !== undefined && historico?.cd_consumo_atual !== null) ||
    (historico?.cd_consumo_anterior !== undefined && historico?.cd_consumo_anterior !== null);

  const validarCamposObrigatorios = () => {
    if (!form.operacao) {
      setError('Selecione a operação para verificar/salvar o pré-ticket.');
      return false;
    }

    if (
      !form.placa ||
      !form.tipoVeiculo ||
      !form.meta3 ||
      !form.motorista ||
      !form.posto ||
      !form.hodometro ||
      !form.hodometroAtual ||
      !form.valorDiesel ||
      (!form.litrosAbastecidos && !form.litrosReais)
    ) {
      setError('Preencha os campos obrigatorios para verificar/salvar.');
      return false;
    }

    const hodometroAnterior = toNumber(form.hodometro, Number.NaN);
    const hodometroAtual = toNumber(form.hodometroAtual, Number.NaN);

    if (!Number.isNaN(hodometroAnterior) && !Number.isNaN(hodometroAtual) && hodometroAtual < hodometroAnterior) {
      setError('Hodometro Atual nao pode ser menor que o Hodometro anterior. Corrija para continuar.');
      return false;
    }

    const motoristaValido = motoristas.some((item) => {
      if (form.motoristaCpf) {
        return item.nome === form.motorista && item.cpf === form.motoristaCpf;
      }

      return item.nome === form.motorista;
    });

    if (!motoristaValido) {
      setError('Motorista deve ser selecionado somente pela lista (query).');
      return false;
    }

    return true;
  };

  const abrirModalVerificacao = async () => {
    clearAlerts();
    if (!validarCamposObrigatorios()) {
      return;
    }

    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/abastecimento/validarNovoHodometro', {
        method: 'POST',
        body: JSON.stringify({
          placa: form.placa,
          tipoAbastecimento: form.arla ? 'Arla' : 'Diesel',
          dataAtual: form.dataAtual,
          hodometroAnterior: toNumber(form.hodometro, 0),
          hodometroAtual: toNumber(form.hodometroAtual, 0)
        })
      });
      const resultado = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(resultado.error || 'Este pre-ticket causaria problemas na sequencia dos hodometros e nao pode ser criado.');
        return;
      }
      setObservacaoAprovacao('');
      setShowAprovacaoModal(true);
    } catch {
      setError('Nao foi possivel validar a sequencia dos hodometros. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const aprovarPreticket = async () => {
    clearAlerts();

    try {
      setLoading(true);

      const valorLitro = toNumber(form.valorDiesel, 0);
      const valorTotal = valorTotalAbastecimento;
      const metaReal = toNumber(form.meta3, 0);

      const payload = {
        codTicketNucci: 0,
        placa: form.placa,
        dataAtual: form.dataAtual,
        tipoVeiculo: form.tipoVeiculo,
        tipoAbastecimento: form.arla ? 'Arla' : 'Diesel',
        posto: form.posto,
        operacao: form.operacao || null,
        qtdeLitros: toNumber(form.litrosReais || form.litrosAbastecidos, 0),
        valorLitro,
        valorTotal,
        consumoAnterior: toNumber(form.consumoAnterior, 0),
        litrosCalculados: litrosIdeais !== null ? Number(litrosIdeais.toFixed(2)) : null,
        litrosReais: form.litrosReais ? toNumber(form.litrosReais, 0) : null,
        consumoAtual: Number(consumoReal.toFixed(2)),
        motorista: form.motorista,
        cpfMotorista: form.motoristaCpf || null,
        metaReal,
        observacao: observacaoAprovacao.trim(),
        hodometroAnterior: toNumber(form.hodometro, 0),
        hodometroAtual: toNumber(form.hodometroAtual, 0),
        distancia: distanciaCalculada
      };

      const response = await authenticatedFetch('/api/abastecimento/savePreticketAbastecimento', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      const resultado = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(resultado.error || 'Erro ao salvar pre-ticket');

      setShowAprovacaoModal(false);
      setForm(getInitialFormState());
      setVehicleType(null);
      setHistorico(null);
      setObservacaoAprovacao('');
      setCopyFeedback('');
      setMessage('Pre-ticket verificado e salvo com sucesso.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao salvar o pre-ticket.');
    } finally {
      setLoading(false);
    }
  };

  const buscarRelatorio = async () => {
    clearAlerts();

    if (!filtrosRelatorio.dataInicio || !filtrosRelatorio.dataFim) {
      setError('Informe data inicial e data final para consultar o relatorio.');
      return;
    }

    try {
      setLoadingRelatorio(true);
      const response = await fetch('/api/abastecimento/relatorioAbastecimento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filtrosRelatorio)
      });

      if (!response.ok) {
        throw new Error('Falha ao carregar relatorio');
      }

      const data = await response.json();
      setRelatorioRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Nao foi possivel buscar o relatorio de abastecimento.');
      setRelatorioRows([]);
    } finally {
      setLoadingRelatorio(false);
    }
  };

  const exportarRelatorio = async () => {
    clearAlerts();
    if (relatorioRows.length === 0) {
      setError('Nao ha dados para exportar. Consulte o relatorio primeiro.');
      return;
    }
    setExportandoRelatorio(true);
    try {
      const workbook = criarRelatorioExcel(relatorioRows);
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, 'relatorio_abastecimento.xlsx');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nao foi possivel exportar o relatorio.');
    } finally {
      setExportandoRelatorio(false);
    }
  };

  return (
    <div className="abastecimento-module">
      <div className="abastecimento-header">
        <h1>Pre-Abastecimento</h1>
        <p>Preencha em 3 etapas: buscar placa, revisar dados base e concluir verificacao.</p>
      </div>

      <div className="abastecimento-top-actions">
        <button type="button" className="ghost-btn" onClick={() => carregarProblemasHodometro(true)} disabled={loadingProblemas}>
          {loadingProblemas ? 'VERIFICANDO...' : `PROBLEMAS DE HODOMETRO${problemasHodometro.length ? ` (${problemasHodometro.length})` : ''}`}
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={() => {
            setShowRelatorioModal(true);
            setRelatorioRows([]);
          }}
        >
          RELATORIO ABASTECIMENTO
        </button>
        <button type="button" className="ghost-btn">CALCULO KM</button>
      </div>

      <div className="section-title">1. Buscar placa</div>
      <div className="abastecimento-search-row">
        <input
          list="lista-placas"
          placeholder="Placa"
          value={form.placa}
          onChange={(e) => setForm((prev) => ({ ...prev, placa: e.target.value.toUpperCase() }))}
        />
        <label className="inline-check">
          <input
            type="checkbox"
            checked={form.arla}
            onChange={(e) => setForm((prev) => ({ ...prev, arla: e.target.checked }))}
          />
          Arla
        </label>
        <button type="button" onClick={buscarDadosPlaca} disabled={loading}>BUSCAR</button>
      </div>

      <div className="search-tip">Dica: marque Arla para carregar metas e historico de ARLA.</div>

      <datalist id="lista-placas">
        {placas.map((placa) => (
          <option key={placa} value={placa} />
        ))}
      </datalist>

      {message && <div className="abastecimento-alert success">{message}</div>}
      {error && <div className="abastecimento-alert error">{error}</div>}

      <section className="abastecimento-card">
        <div className="section-title">2. Revisar dados de abastecimento</div>
        <div className="abastecimento-grid-two">
          <div className="abastecimento-col">
            <label>
              Tipo Veiculo
              <input
                value={form.tipoVeiculo}
                onChange={(e) => setForm((prev) => ({ ...prev, tipoVeiculo: e.target.value }))}
                placeholder="Ex.: Cavalo 6x2"
                readOnly={isTipoVeiculoLocked}
                className={isTipoVeiculoLocked ? 'readonly-field' : ''}
              />
            </label>

            <label>
              Data Abastecimento Anterior
              <input
                type="datetime-local"
                value={form.dataAbastecimento}
                onChange={(e) => setForm((prev) => ({ ...prev, dataAbastecimento: e.target.value }))}
                readOnly={isDataAbastecimentoLocked}
                className={isDataAbastecimentoLocked ? 'readonly-field' : ''}
              />
            </label>

            <label>
              Litros Abastecidos (Atual)
              <input
                type="number"
                step="0.01"
                value={form.litrosAbastecidos}
                onChange={(e) => setForm((prev) => ({ ...prev, litrosAbastecidos: e.target.value }))}
                placeholder="Ex.: 350"
                readOnly={isLitrosAbastecidosLocked}
                className={isLitrosAbastecidosLocked ? 'readonly-field' : ''}
              />
            </label>

            <label>
              Valor Litros
              <input
                type="number"
                step="0.0001"
                value={form.valorLitros}
                onChange={(e) => setForm((prev) => ({ ...prev, valorLitros: e.target.value }))}
                placeholder="Ex.: 5.69"
                readOnly={isValorLitrosLocked}
                className={isValorLitrosLocked ? 'readonly-field' : ''}
              />
            </label>

            <label>
              Hodometro
              <input
                type="number"
                value={form.hodometro}
                onChange={(e) => setForm((prev) => ({ ...prev, hodometro: e.target.value }))}
                placeholder="Ex.: 512340"
                readOnly={isHodometroLocked}
                className={isHodometroLocked ? 'readonly-field' : ''}
              />
            </label>

            <label>
              Consumo Anterior
              <input
                type="text"
                inputMode="decimal"
                value={form.consumoAnterior}
                onChange={(e) => setForm((prev) => ({ ...prev, consumoAnterior: e.target.value }))}
                placeholder="Ex.: 2.95"
                readOnly={isConsumoAnteriorLocked}
                className={isConsumoAnteriorLocked ? 'readonly-field' : ''}
              />
            </label>
          </div>

          <div className="abastecimento-col meta-col">
            <div className="meta-box">Meta 3: {form.meta3 || '-'}</div>
            <div className="meta-info">Meta veiculo: {formatNumber(form.arla ? vehicleType?.META3_ARLA : vehicleType?.META3)}</div>
            <div className="meta-info">Consumo sistema: {formatNumber(consumoSistema)} km/l</div>
            <div className="meta-info">Consumo real: {formatNumber(consumoReal)} km/l</div>
            <div className="meta-info">Distancia calculada: {formatNumber(distanciaCalculada, 0)} km</div>
          </div>
        </div>

        <hr className="abastecimento-divider" />

        <div className="section-title">3. Fechamento do pre-ticket</div>
        <div className="abastecimento-grid-bottom">
          <label>
            Motorista
            <input
              list="lista-motoristas"
              value={form.motoristaCpf ? `${form.motorista} - ${formatCpf(form.motoristaCpf)}` : form.motorista}
              onChange={(e) => {
                const inputValue = e.target.value;
                const cpfDigits = normalizeCpf(inputValue);

                if (cpfDigits.length >= 6) {
                  const cpfMatches = getCpfPrefixMatches(cpfDigits);
                  if (cpfMatches.length === 1) {
                    applyMotoristaOption(cpfMatches[0]);
                    return;
                  }
                }

                const found = motoristas.find((item) => item.label === inputValue);

                if (found) {
                  setForm((prev) => ({
                    ...prev,
                    motorista: found.nome,
                    motoristaCpf: found.cpf
                  }));
                  return;
                }

                const parsed = splitMotoristaLabel(inputValue);
                setForm((prev) => ({
                  ...prev,
                  motorista: parsed.nome,
                  motoristaCpf: parsed.cpf
                }));
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') {
                  return;
                }

                e.preventDefault();
                confirmMotoristaInput((e.target as HTMLInputElement).value);
              }}
              onBlur={(e) => {
                confirmMotoristaInput(e.target.value, isCpfValue(e.target.value));
              }}
              placeholder="Nome - CPF do motorista"
            />
          </label>

          <label>
            Posto
            <input
              list="lista-postos"
              value={form.posto}
              onChange={(e) => setForm((prev) => ({ ...prev, posto: e.target.value }))}
              placeholder="Nome do posto"
            />
          </label>

          <label>
            Operação *
            <select
              required
              value={form.operacao}
              onChange={(e) => setForm((prev) => ({ ...prev, operacao: e.target.value }))}
            >
              <option value="">Selecione a operação</option>
              <option value="Eldorado">Eldorado</option>
              <option value="Volks">Volks</option>
              <option value="Brose">Brose</option>
            </select>
          </label>

          <label>
            Data Atual
            <input
              type="datetime-local"
              value={form.dataAtual}
              onChange={(e) => setForm((prev) => ({ ...prev, dataAtual: e.target.value }))}
            />
          </label>

          <label>
            Hodometro Atual *
            <input
              type="number"
              value={form.hodometroAtual}
              onChange={(e) => setForm((prev) => ({ ...prev, hodometroAtual: e.target.value }))}
              placeholder="Obrigatorio"
            />
          </label>

          <label>
            Valor Diesel *
            <input
              type="number"
              step="0.0001"
              value={form.valorDiesel}
              onChange={(e) => setForm((prev) => ({ ...prev, valorDiesel: e.target.value }))}
              placeholder="Obrigatorio"
            />
          </label>

          <label>
            Litros
            <input
              type="text"
              value={litrosIdeais !== null ? formatNumber(litrosIdeais) : ''}
              readOnly
              className="readonly-field"
              placeholder="Calculado automaticamente"
            />
          </label>

          <label>
            Litros Reais
            <input
              type="number"
              step="0.01"
              value={form.litrosReais}
              onChange={(e) => setForm((prev) => ({ ...prev, litrosReais: e.target.value }))}
            />
          </label>

          <button type="button" onClick={abrirModalVerificacao} disabled={loading}>
            {loading ? 'PROCESSANDO...' : 'VERIFICAR'}
          </button>
        </div>

        <div className="abastecimento-footer-tip">
          Campos com * sao obrigatorios para salvar o pre-ticket.
        </div>

        <datalist id="lista-motoristas">
          {motoristas.map((item) => (
            <option key={`${item.nome}-${item.cpf || 'sem-cpf'}`} value={item.label} />
          ))}
        </datalist>

        <datalist id="lista-postos">
          {postos.map((posto) => (
            <option key={posto} value={posto} />
          ))}
        </datalist>
      </section>

      {showAprovacaoModal && (
        <div className="abastecimento-modal-overlay" role="dialog" aria-modal="true" aria-label="Dados do pre-ticket">
          <div className="abastecimento-modal">
            <h2>Dados do Pre-Ticket</h2>
            <div className="abastecimento-modal-divider" />

            <div className="preticket-modal-grid">
              <section className="preticket-modal-card card-veiculo">
                <h3>Veiculo/Motorista</h3>
                <div className="preticket-modal-card-content">
                  <p><strong>Placa:</strong> {form.placa || '-'}</p>
                  <p><strong>Motorista:</strong> {form.motorista || '-'}</p>
                  <p><strong>Tipo:</strong> {form.tipoVeiculo || '-'}</p>
                </div>
              </section>

              <section className="preticket-modal-card card-abastecimento">
                <h3>Abastecimento</h3>
                <div className="preticket-modal-card-content">
                  <p><strong>Tipo:</strong> {form.arla ? 'Arla' : 'Diesel'}</p>
                  <p><strong>Posto:</strong> {form.posto || '-'}</p>
                  <p><strong>Operação:</strong> {form.operacao || '-'}</p>
                  <p><strong>Litros Ideal:</strong> {litrosIdeais !== null ? `${formatNumber(litrosIdeais)} L` : '-'}</p>
                  <p><strong>Litros Realizado:</strong> {form.litrosReais ? `${formatNumber(form.litrosReais)} L` : `${formatNumber(form.litrosAbastecidos)} L`}</p>
                  <p><strong>Valor/L:</strong> {formatCurrency(toNumber(form.valorDiesel || form.valorLitros, 0))}</p>
                  <p className="preticket-total"><strong>Total:</strong> {formatCurrency(valorTotalAbastecimento)}</p>
                </div>
              </section>

              <section className="preticket-modal-card card-consumo full-width">
                <h3>Consumo</h3>
                <div className="preticket-modal-card-content consumo-grid">
                  <p><strong>Anterior:</strong> {formatNumber(form.consumoAnterior, 3)}</p>
                  <p><strong>Desejado:</strong> {formatNumber(form.meta3, 2)}</p>
                  <p>
                    <strong>Real:</strong> {formatNumber(consumoReal, 2)}
                    {toNumber(form.meta3, 0) > 0 && consumoReal > toNumber(form.meta3, 0) ? '' : ''}
                  </p>
                </div>
              </section>

              <section className="preticket-modal-card card-rodagem full-width">
                <h3>Rodagem</h3>
                <div className="preticket-modal-card-content">
                  <p>
                    <strong>Hodometro:</strong> {formatNumber(form.hodometro, 0)} {'->'} {formatNumber(form.hodometroAtual, 0)}
                    {' | '}
                    <strong>Distancia:</strong> {formatNumber(distanciaCalculada, 0)} km
                  </p>
                </div>
              </section>
            </div>

            <label className="abastecimento-modal-observacao">
              Observacao
              <textarea
                rows={3}
                value={observacaoAprovacao}
                onChange={(e) => setObservacaoAprovacao(e.target.value)}
                placeholder="Observacao (opcional)"
              />
            </label>

            <div className="abastecimento-modal-actions">
              <button type="button" className="btn-copiar" onClick={copiarDadosPreticket}>
                COPIAR
              </button>
              <button type="button" className="btn-cancelar" onClick={() => setShowAprovacaoModal(false)} disabled={loading}>
                CANCELAR
              </button>
              <button type="button" className="btn-aprovar" onClick={aprovarPreticket} disabled={loading}>
                {loading ? 'APROVANDO...' : 'APROVAR'}
              </button>
            </div>
            {copyFeedback && <div className="abastecimento-copy-feedback">{copyFeedback}</div>}
          </div>
        </div>
      )}

      {showRelatorioModal && (
        <div className="abastecimento-modal-overlay" role="dialog" aria-modal="true" aria-label="Relatorio de abastecimento">
          <div className="abastecimento-modal abastecimento-relatorio-modal">
            <h2>Relatorio Abastecimento</h2>
            <div className="abastecimento-modal-divider" />

            <div className="abastecimento-relatorio-filtros">
              <label>
                Data Inicio
                <input
                  type="date"
                  value={filtrosRelatorio.dataInicio}
                  onChange={(e) => setFiltrosRelatorio((prev) => ({ ...prev, dataInicio: e.target.value }))}
                />
              </label>

              <label>
                Data Fim
                <input
                  type="date"
                  value={filtrosRelatorio.dataFim}
                  onChange={(e) => setFiltrosRelatorio((prev) => ({ ...prev, dataFim: e.target.value }))}
                />
              </label>

              <label>
                Placa
                <input
                  list="lista-placas"
                  value={filtrosRelatorio.placa}
                  onChange={(e) => setFiltrosRelatorio((prev) => ({ ...prev, placa: e.target.value.toUpperCase() }))}
                  placeholder="Todas"
                />
              </label>

              <label>
                Operação
                <select
                  value={filtrosRelatorio.operacao}
                  onChange={(e) => setFiltrosRelatorio((prev) => ({ ...prev, operacao: e.target.value }))}
                >
                  <option value="">Todas</option>
                  <option value="Eldorado">Eldorado</option>
                  <option value="Volks">Volks</option>
                  <option value="Brose">Brose</option>
                </select>
              </label>

              <button type="button" className="btn-aprovar" onClick={buscarRelatorio} disabled={loadingRelatorio}>
                {loadingRelatorio ? 'CONSULTANDO...' : 'CONSULTAR'}
              </button>

              <button type="button" className="ghost-btn" onClick={exportarRelatorio} disabled={loadingRelatorio || exportandoRelatorio || relatorioRows.length === 0}>
                {exportandoRelatorio ? 'EXPORTANDO...' : 'EXPORTAR XLSX'}
              </button>
            </div>

            <div className="abastecimento-relatorio-tabela">
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Placa</th>
                    <th>Tipo Veiculo</th>
                    <th>Tipo Combustivel</th>
                    <th>Posto</th>
                    <th>Operação</th>
                    <th>Hodometro Ant.</th>
                    <th>Hodometro Atual</th>
                    <th>Valor Litro</th>
                    <th>Valor Abastecimento</th>
                    <th>Quantidade Calculado</th>
                    <th>Quantidade Abastecido</th>
                    <th>Motorista</th>
                    <th>Consumo</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorioRows.length === 0 && (
                    <tr>
                      <td colSpan={14} className="abastecimento-empty">Nenhum dado encontrado.</td>
                    </tr>
                  )}

                  {relatorioRows.map((row, index) => (
                    <tr key={`${row.ID ?? index}-${row.DATA ?? ''}`}>
                      <td>{formatDateTime(row.DATA)}</td>
                      <td>{row.PLACA || '-'}</td>
                      <td>{row.TIPO_VEICULO || '-'}</td>
                      <td>{row.TIPO_COMBUSTIVEL || '-'}</td>
                      <td>{row.POSTO || '-'}</td>
                      <td>{row.OPERACAO || '-'}</td>
                      <td>{formatNumber(row.HODOMETRO_ANTERIOR, 0)}</td>
                      <td>{formatNumber(row.HODOMETRO_ATUAL, 0)}</td>
                      <td>{formatCurrency(row.VALOR_LITRO)}</td>
                      <td>{formatCurrency(row.VALOR_ABASTECIMENTO)}</td>
                      <td>{formatNumber(row.QUANTIDADE_CALCULADO)}</td>
                      <td>{formatNumber(row.QUANTIDADE_ABASTECIDO)}</td>
                      <td>{row.MOTORISTA || '-'}</td>
                      <td>{formatNumber(row.CONSUMO)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="abastecimento-modal-actions">
              <button type="button" className="btn-cancelar" onClick={() => setShowRelatorioModal(false)}>
                FECHAR
              </button>
            </div>
          </div>
        </div>
      )}

      {showProblemasModal && (
        <div className="abastecimento-modal-overlay" role="dialog" aria-modal="true" aria-label="Problemas de hodometro">
          <div className="abastecimento-modal abastecimento-problemas-modal">
            <h2>Problemas na sequencia de hodometros</h2>
            <div className="abastecimento-modal-divider" />
            <p className="problemas-explicacao">
              Esta tela e somente informativa. Confira no ticket original a data e os hodometros dos lancamentos indicados antes de corrigir os dados na fonte responsavel.
            </p>
            {message && <div className="abastecimento-alert success">{message}</div>}
            {error && <div className="abastecimento-alert error">{error}</div>}

            <div className="problemas-toolbar">
              <button type="button" className="ghost-btn" onClick={() => carregarProblemasHodometro()} disabled={loadingProblemas}>ATUALIZAR LISTA</button>
            </div>

            {problemasHodometro.length === 0 ? (
              <div className="abastecimento-empty">Nenhum problema encontrado na sequencia.</div>
            ) : (
              <div className="problemas-lista">
                {problemasHodometro.map((problema) => (
                  <section key={`${problema.placa}-${problema.tipoAbastecimento}`} className="problema-card manual">
                    <div className="problema-card-header">
                      <strong>{problema.placa} - {problema.tipoAbastecimento}</strong>
                      <span>{problema.totalTicketsAfetados} TICKET(S) AFETADO(S)</span>
                    </div>
                    <p>{problema.orientacao}</p>
                    <div className="problema-comparacao">
                      <div>
                        <small>Ticket anterior de referencia</small>
                        <strong>ID {problema.registroReferencia.cd_abastecimento} — {formatDateTime(problema.registroReferencia.data_referencia)}</strong>
                        <span>Hodometro atual: {formatNumber(problema.registroReferencia.cd_hodometro_atual, 0)}</span>
                      </div>
                      <div>
                        <small>Primeiro ticket onde o problema comecou</small>
                        <strong>ID {problema.primeiroTicketProblema.cd_abastecimento} — {formatDateTime(problema.primeiroTicketProblema.data_referencia)}</strong>
                        <span>Hodometro anterior: {formatNumber(problema.primeiroTicketProblema.cd_hodometro_anterior, 0)}</span>
                        <span>Hodometro atual: {formatNumber(problema.primeiroTicketProblema.cd_hodometro_atual, 0)}</span>
                        {problema.primeiroTicketProblema.cd_ticket_nucci ? <span>Ticket NUCCI: {problema.primeiroTicketProblema.cd_ticket_nucci}</span> : null}
                      </div>
                    </div>

                    <h4>Tickets posteriores potencialmente impactados</h4>
                    {problema.ticketsImpactados.length === 0 ? (
                      <div className="abastecimento-empty">Nao existem tickets posteriores nesta cadeia.</div>
                    ) : (
                      <div className="problemas-tickets-table">
                        <table>
                          <thead><tr><th>ID</th><th>Data</th><th>Hodometro anterior</th><th>Hodometro atual</th><th>Distancia</th></tr></thead>
                          <tbody>
                            {problema.ticketsImpactados.map((ticket) => (
                              <tr key={ticket.cd_abastecimento}>
                                <td>{ticket.cd_abastecimento}</td>
                                <td>{formatDateTime(ticket.data_referencia)}</td>
                                <td>{formatNumber(ticket.cd_hodometro_anterior, 0)}</td>
                                <td>{formatNumber(ticket.cd_hodometro_atual, 0)}</td>
                                <td>{formatNumber(ticket.cd_distancia, 0)} km</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                ))}
              </div>
            )}

            <div className="abastecimento-modal-actions">
              <button type="button" className="btn-cancelar" onClick={() => setShowProblemasModal(false)}>FECHAR</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbastecimentoModule;
